import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Edit2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";

const EMPTY = { code: "", name: "", category: "I përgjithshëm", default_cost: 0, duration_min: 30, notes: "" };

export default function Procedures() {
  const { user } = useAuth();
  const { format, symbol } = useCurrency();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const canEdit = ["admin", "doctor"].includes(user?.role);

  const load = async () => {
    const { data } = await api.get("/procedures");
    setList(data);
  };
  useEffect(() => { load(); }, []);

  const categories = Array.from(new Set(list.map((p) => p.category))).sort();
  const [cat, setCat] = useState("all");
  const filtered = cat === "all" ? list : list.filter((p) => p.category === cat);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/procedures/${editing.id}`, form);
      else await api.post(`/procedures`, form);
      toast.success(editing ? "Procedura u përditësua" : "Procedura u shtua");
      setOpen(false); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Fshini këtë procedurë?")) return;
    try { await api.delete(`/procedures/${id}`); toast.success("U fshi"); load(); }
    catch { toast.error("Nuk keni leje"); }
  };

  return (
    <div className="space-y-8" data-testid="procedures-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Katalogu</div>
          <h1 className="mt-2 text-4xl font-semibold tracking-tight sm:text-5xl">Procedurat</h1>
          <p className="mt-2 text-sm text-[#5C5C5C]">Lista e procedurave standarde me kode dhe çmime referencë.</p>
        </div>
        {canEdit && (
          <button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="btn-primary" data-testid="add-procedure-button">
            <Plus size={16} /> Procedurë e Re
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <FilterChip label="Të gjitha" active={cat === "all"} onClick={() => setCat("all")} />
        {categories.map((c) => <FilterChip key={c} label={c} active={cat === c} onClick={() => setCat(c)} />)}
      </div>

      <div className="surface-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FDFBF7] text-left">
            <tr>
              <th className="px-6 py-3 label-eyebrow">Kodi</th>
              <th className="px-6 py-3 label-eyebrow">Emri</th>
              <th className="px-6 py-3 label-eyebrow">Kategoria</th>
              <th className="px-6 py-3 label-eyebrow text-right">Kohëzgjatja</th>
              <th className="px-6 py-3 label-eyebrow text-right">Çmimi Ref.</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} className="border-t border-[#E2DFD8]">
                <td className="px-6 py-3 font-mono text-xs text-[#5C5C5C]">{p.code || "—"}</td>
                <td className="px-6 py-3 font-medium">{p.name}</td>
                <td className="px-6 py-3">
                  <span className="rounded-full bg-[#EAE4D9] px-3 py-1 text-xs font-medium text-[#2C4C3B]">{p.category}</span>
                </td>
                <td className="px-6 py-3 text-right">{p.duration_min} min</td>
                <td className="px-6 py-3 text-right font-mono">{format(p.default_cost)}</td>
                <td className="px-6 py-3 text-right">
                  {canEdit && (
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => { setEditing(p); setForm(p); setOpen(true); }} className="p-1.5 rounded hover:bg-[#EAE4D9]">
                        <Edit2 size={14} />
                      </button>
                      {user.role === "admin" && (
                        <button onClick={() => remove(p.id)} className="p-1.5 rounded hover:bg-[#9E3C3C]/10 text-[#9E3C3C]">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-xl">
          <DialogHeader><DialogTitle>{editing ? "Përditëso" : "Shto"} Procedurë</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <Field label="Kodi (opsionale)"><Input value={form.code} onChange={(e) => setForm({...form, code: e.target.value})} placeholder="P.sh. D2330" /></Field>
            <Field label="Kategoria"><Input value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} /></Field>
            <div className="md:col-span-2"><Field label="Emri"><Input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></Field></div>
            <Field label={`Çmimi Referencë (${symbol})`}><Input type="number" step="0.01" value={form.default_cost} onChange={(e) => setForm({...form, default_cost: Number(e.target.value)})} /></Field>
            <Field label="Kohëzgjatja (min)"><Input type="number" value={form.duration_min} onChange={(e) => setForm({...form, duration_min: Number(e.target.value)})} /></Field>
            <div className="md:col-span-2"><Field label="Shënime"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} /></Field></div>
            <DialogFooter className="md:col-span-2">
              <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>Anulo</button>
              <button type="submit" className="btn-primary">{editing ? "Përditëso" : "Ruaj"}</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (<div><Label className="label-eyebrow">{label}</Label><div className="mt-2">{children}</div></div>);
}
function FilterChip({ label, active, onClick }) {
  return (
    <button onClick={onClick}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              active ? "bg-[#2C4C3B] text-[#FDFBF7]" : "bg-white border border-[#E2DFD8] text-[#5C5C5C] hover:text-[#2C4C3B]"
            }`}>{label}</button>
  );
}
